import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Alert, Badge } from 'react-bootstrap';
import { Settings, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import BuildingCheckTemplateModal from './BuildingCheckTemplateModal';
import { BuildingCheckTemplate, INTERVAL_UNITS } from './types';
import { BuildingCheck, IntervalUnit, componentsCoveredByTemplate, formatInterval } from './buildingChecksUtils';
import { translateInspectionCategory, translateRegulatoryType, translate } from '../../types/EnumTranslations';
import { buildingChecksApi, NewBuildingCheck } from '../../api/buildingChecksApi';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { BaseComponentImpl } from '../../types/implementations';
import { getComponentRoomKey } from '../../Helpers/ComponentRoomUtils';
import { getBaseTypeOrder } from '../../Helpers/baseTypeUtils';
import { BaseType } from '../../types/Enums';
import { BlueprintLocation } from '../../types/protocols';
import { LocationMarkerPicker } from './components';
import { unitsApi } from '../../api/unitsApi';
import { sortedUnits, unitDisplayName } from '../../Helpers/unitUtils';
import { isLiveComponent } from '../../Helpers/assessmentGroups';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import './BuildingChecksPage.css';
import NumberInput from '../../Components/NumberInput';
import { useFeature } from '../../hooks/useFeatures';

// 'components' creates one check per selected component; 'none', 'location' and 'unit'
// create one building-wide check
type LinkingMode = 'none' | 'components' | 'location' | 'unit';

interface ComponentItem {
    key: string;
    specificType?: string;
    component: BaseComponentImpl;
    displayName: string;
    baseType: BaseType;
    levelKey?: string;
    levelName?: string;
    roomKey?: string;
    roomName?: string;
}

const AddBuildingCheckPage: React.FC = () => {
    const { id: buildingId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentBuilding } = useBuilding();
    const { getTranslation } = useBuildComponentTypes();
    // Templates are admin-write (rule), so only admins may create one from here
    const isAdmin = useIsAdmin();

    useDocumentTitle(currentBuilding?.summary?.name ? `Lägg till aktivitet - ${currentBuilding.summary.name}` : 'Lägg till aktivitet');

    // Template and existing checks
    const [templates, setTemplates] = useState<BuildingCheckTemplate[]>([]);
    const [existingChecks, setExistingChecks] = useState<Record<string, BuildingCheck>>({});
    const [loading, setLoading] = useState(true);
    const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);

    // Form state
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [label, setLabel] = useState<string>('');
    const [buildingNotes, setBuildingNotes] = useState<string>('');
    const [useCustomInterval, setUseCustomInterval] = useState(false);
    const [customIntervalValue, setCustomIntervalValue] = useState<number>(1);
    const [customIntervalUnit, setCustomIntervalUnit] = useState<IntervalUnit>('months');
    const [customCost, setCustomCost] = useState<string>('');
    const [linkingMode, setLinkingMode] = useState<LinkingMode>('none');
    const [selectedComponentKeys, setSelectedComponentKeys] = useState<string[]>([]);
    // The template's component type pre-filters the picker; the filter can be removed
    const [filterByType, setFilterByType] = useState(true);
    const [selectedUnitKey, setSelectedUnitKey] = useState<string>('');
    // No unit picker while Lägenheter & lokaler is off (Inställningar → Funktioner)
    const unitsEnabled = useFeature('units');
    const unitOptions = useMemo(
        () => (unitsEnabled ? sortedUnits(unitsApi.parseUnits(currentBuilding?.model?.units)) : []),
        [currentBuilding, unitsEnabled],
    );
    const [selectedLocation, setSelectedLocation] = useState<{
        type: 'blueprint' | 'satellite';
        sourceKey: string;
        marker: BlueprintLocation;
    } | undefined>(undefined);

    // Component tree state
    const [collapsedBaseTypes, setCollapsedBaseTypes] = useState<Set<string>>(new Set());
    const [collapsedLevels, setCollapsedLevels] = useState<Set<string>>(new Set());
    const [collapsedRooms, setCollapsedRooms] = useState<Set<string>>(new Set());

    // UI state
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    // Get selected template
    const selectedTemplate = useMemo(() => {
        return templates.find(t => t.id === selectedTemplateId) || null;
    }, [templates, selectedTemplateId]);

    // Every template can have several checks on a building. Skickbedömning (group templates)
    // has one fixed check per building, scheduled when the maintenance plan is activated.
    const availableTemplates = useMemo(() => {
        return templates
            .filter(t => !t.componentBaseTypeGroup)
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [templates]);

    // Build component items for the tree
    const componentItems = useMemo((): ComponentItem[] => {
        if (!currentBuilding?.model?.structure?.components) return [];

        const levels = currentBuilding.model.structure?.levels || {};
        const rooms = currentBuilding.model.structure?.rooms || {};

        return Object.entries(currentBuilding.model.structure.components)
            .map(([key, componentData]): ComponentItem | null => {
                const component = componentData as BaseComponentImpl;
                if (!component.type || !isLiveComponent(component)) return null;

                // Find room and level info
                let levelKey: string | undefined;
                let levelName: string | undefined;
                let roomKey: string | undefined;
                let roomName: string | undefined;

                // Check if component is in a room
                const roomLocation = getComponentRoomKey(component);
                if (roomLocation && rooms[roomLocation]) {
                    const room = rooms[roomLocation];
                    roomKey = roomLocation;
                    roomName = room.label || (room.number ? `Rum ${room.number}` : `Rum ${roomLocation}`);
                    levelKey = room.levelKey;
                    if (levelKey && levels[levelKey]) {
                        levelName = levels[levelKey].name || `Plan ${levels[levelKey].number}`;
                    }
                }

                // Create a BaseComponentImpl to access displayName method
                const componentImpl = new BaseComponentImpl(component);

                return {
                    key,
                    specificType: component.specificType?.value ?? undefined,
                    component,
                    displayName: currentBuilding ? componentImpl.displayName(currentBuilding) : key,
                    baseType: component.type,
                    levelKey,
                    levelName,
                    roomKey,
                    roomName
                };
            })
            .filter((item): item is ComponentItem => item !== null);
    }, [currentBuilding]);

    // A component is in at most one check per template: those the template's checks
    // already cover are not offered
    const coveredKeys = useMemo(
        () => (selectedTemplate ? componentsCoveredByTemplate(selectedTemplate.id, existingChecks) : new Set<string>()),
        [selectedTemplate, existingChecks]
    );

    const suggestedType = selectedTemplate?.componentSpecificType;
    const typeFilterActive = Boolean(suggestedType) && filterByType;

    // The components offered: free for the template, of its type while the filter is on
    const offeredItems = useMemo(
        () => componentItems.filter(item =>
            !coveredKeys.has(item.key) && (!typeFilterActive || item.specificType === suggestedType)),
        [componentItems, coveredKeys, typeFilterActive, suggestedType]
    );

    // Group components by BaseType -> Level -> Room
    const groupedComponents = useMemo(() => {
        const groups: Map<BaseType, Map<string, Map<string, ComponentItem[]>>> = new Map();

        offeredItems.forEach(item => {
            if (!groups.has(item.baseType)) {
                groups.set(item.baseType, new Map());
            }
            const baseTypeGroup = groups.get(item.baseType)!;

            const levelId = item.levelKey || '_no_level';
            if (!baseTypeGroup.has(levelId)) {
                baseTypeGroup.set(levelId, new Map());
            }
            const levelGroup = baseTypeGroup.get(levelId)!;

            const roomId = item.roomKey || '_no_room';
            if (!levelGroup.has(roomId)) {
                levelGroup.set(roomId, []);
            }
            levelGroup.get(roomId)!.push(item);
        });

        // Sort components within each room
        groups.forEach(baseTypeGroup => {
            baseTypeGroup.forEach(levelGroup => {
                levelGroup.forEach(components => {
                    components.sort((a, b) => a.displayName.localeCompare(b.displayName, 'sv'));
                });
            });
        });

        return groups;
    }, [offeredItems]);

    // Load templates and existing checks
    useEffect(() => {
        const loadData = async () => {
            if (!buildingId) return;

            try {
                const [loadedTemplates, checks] = await Promise.all([
                    buildingChecksApi.loadTemplates(),
                    buildingChecksApi.loadBuildingCheckInstances(buildingId)
                ]);
                setTemplates(Object.values(loadedTemplates));
                setExistingChecks(checks);
            } catch (error) {
                console.error('Error loading data:', error);
                setAlert({ type: 'danger', message: 'Kunde inte ladda data' });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [buildingId]);

    // Template picked: its component type is only a suggestion (pre-filters the picker)
    const applyTemplate = (template: BuildingCheckTemplate | null) => {
        setSelectedTemplateId(template?.id ?? '');
        setSelectedComponentKeys([]);
        setFilterByType(true);
        setLinkingMode(template?.componentSpecificType ? 'components' : 'none');
    };

    const selectTemplate = (templateId: string) => {
        applyTemplate(templates.find(t => t.id === templateId) ?? null);
    };

    // A template created here is applied to the check being added
    const handleNewTemplateSaved = (template: BuildingCheckTemplate) => {
        setShowNewTemplateModal(false);
        setTemplates(prev => [...prev.filter(t => t.id !== template.id), template]);
        applyTemplate(template);
    };

    const offeredKeys = offeredItems.map(item => item.key);
    const allOfferedSelected = offeredKeys.length > 0 && offeredKeys.every(key => selectedComponentKeys.includes(key));
    const toggleAllOffered = () => {
        setSelectedComponentKeys(prev => (allOfferedSelected
            ? prev.filter(key => !offeredKeys.includes(key))
            : Array.from(new Set([...prev, ...offeredKeys]))));
    };

    // Turning the type filter on drops selected components of other types
    const changeTypeFilter = (on: boolean) => {
        setFilterByType(on);
        if (on && suggestedType) {
            const ofType = new Set(componentItems.filter(item => item.specificType === suggestedType).map(item => item.key));
            setSelectedComponentKeys(prev => prev.filter(key => ofType.has(key)));
        }
    };

    // Toggle component selection
    const toggleComponent = (componentKey: string) => {
        setSelectedComponentKeys(prev => {
            if (prev.includes(componentKey)) {
                return prev.filter(k => k !== componentKey);
            }
            return [...prev, componentKey];
        });
    };

    // Toggle base type collapsed state
    const toggleBaseTypeCollapsed = (baseType: BaseType) => {
        setCollapsedBaseTypes(prev => {
            const next = new Set(prev);
            if (next.has(baseType)) {
                next.delete(baseType);
            } else {
                next.add(baseType);
            }
            return next;
        });
    };

    // Toggle level collapsed state
    const toggleLevelCollapsed = (levelKey: string) => {
        setCollapsedLevels(prev => {
            const next = new Set(prev);
            if (next.has(levelKey)) {
                next.delete(levelKey);
            } else {
                next.add(levelKey);
            }
            return next;
        });
    };

    // Toggle room collapsed state
    const toggleRoomCollapsed = (roomKey: string) => {
        setCollapsedRooms(prev => {
            const next = new Set(prev);
            if (next.has(roomKey)) {
                next.delete(roomKey);
            } else {
                next.add(roomKey);
            }
            return next;
        });
    };

    // Count components in a group
    const countComponentsInBaseType = (baseType: BaseType): number => {
        const group = groupedComponents.get(baseType);
        if (!group) return 0;
        let count = 0;
        group.forEach(levelGroup => {
            levelGroup.forEach(components => {
                count += components.length;
            });
        });
        return count;
    };

    const countComponentsInLevel = (baseType: BaseType, levelKey: string): number => {
        const baseTypeGroup = groupedComponents.get(baseType);
        if (!baseTypeGroup) return 0;
        const levelGroup = baseTypeGroup.get(levelKey);
        if (!levelGroup) return 0;
        let count = 0;
        levelGroup.forEach(components => {
            count += components.length;
        });
        return count;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!selectedTemplate || !startDate || !buildingId) {
            setAlert({ type: 'danger', message: 'Vänligen fyll i alla obligatoriska fält' });
            return;
        }
        const componentKeys = linkingMode === 'components' ? selectedComponentKeys.filter(key => !coveredKeys.has(key)) : [];
        if (linkingMode === 'components' && componentKeys.length === 0) {
            setAlert({ type: 'danger', message: 'Välj minst en komponent' });
            return;
        }

        setSaving(true);
        setAlert(null);

        try {
            const check: NewBuildingCheck = {
                templateId: selectedTemplate.id,
                startDate: startDate,
                nextDueDate: startDate
            };
            // A label tells one check apart; several new checks are named after their components
            if (label.trim() && componentKeys.length <= 1) check.label = label.trim();
            if (buildingNotes.trim()) check.buildingNotes = buildingNotes.trim();
            if (useCustomInterval) {
                check.customInterval = { value: customIntervalValue, unit: customIntervalUnit };
            }
            if (customCost.trim()) {
                const costValue = parseFloat(customCost);
                if (!isNaN(costValue) && costValue >= 0) {
                    check.customCost = costValue;
                }
            }

            if (linkingMode === 'location' && selectedLocation) {
                check.location = selectedLocation;
            }

            // Unit link (phase 3): key plus a label snapshot, like leases and work orders
            if (linkingMode === 'unit' && selectedUnitKey) {
                const unit = unitOptions.find(u => u.key === selectedUnitKey);
                if (unit) {
                    check.unitKey = unit.key;
                    check.unitLabel = unitDisplayName(unit);
                }
            }

            // One check per component (or one without a component), in one write
            await buildingChecksApi.createBuildingChecks(
                buildingId,
                componentKeys.length > 0 ? componentKeys.map(componentKey => ({ ...check, componentKey })) : [check]
            );

            setAlert({
                type: 'success',
                message: componentKeys.length > 1 ? `${componentKeys.length} kontroller tillagda!` : 'Aktivitet tillagd!'
            });

            // Navigate back after a short delay
            setTimeout(() => {
                navigate(`/building/${buildingId}/checks`);
            }, 1000);

        } catch (error) {
            console.error('Error saving check:', error);
            setAlert({ type: 'danger', message: 'Kunde inte spara aktiviteten. Försök igen.' });
        } finally {
            setSaving(false);
        }
    };

    const componentLabel = (comp: ComponentItem) => comp.displayName;

    // Render component tree
    const renderComponentTree = () => {
        const sortedBaseTypes = Array.from(groupedComponents.keys())
            .sort((a, b) => getBaseTypeOrder(a) - getBaseTypeOrder(b));

        return (
            <div className="component-tree border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {sortedBaseTypes.map(baseType => {
                    const isCollapsed = collapsedBaseTypes.has(baseType);
                    const count = countComponentsInBaseType(baseType);
                    const baseTypeGroup = groupedComponents.get(baseType)!;

                    return (
                        <div key={baseType} className="mb-2">
                            <div
                                className="d-flex align-items-center cursor-pointer py-1"
                                onClick={() => toggleBaseTypeCollapsed(baseType)}
                                style={{ cursor: 'pointer' }}
                            >
                                {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                <span className="fw-bold ms-1">{translate(baseType)}</span>
                                <Badge bg="secondary" className="ms-2">{count}</Badge>
                            </div>

                            {!isCollapsed && (
                                <div className="ms-4">
                                    {Array.from(baseTypeGroup.keys()).map(levelKey => {
                                        const levelGroup = baseTypeGroup.get(levelKey)!;
                                        const levelName = levelKey === '_no_level'
                                            ? 'Utan plan'
                                            : componentItems.find(c => c.levelKey === levelKey)?.levelName || levelKey;
                                        const levelCount = countComponentsInLevel(baseType, levelKey);
                                        const levelCollapsed = collapsedLevels.has(`${baseType}_${levelKey}`);

                                        return (
                                            <div key={levelKey} className="mb-1">
                                                <div
                                                    className="d-flex align-items-center cursor-pointer py-1"
                                                    onClick={() => toggleLevelCollapsed(`${baseType}_${levelKey}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {levelCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                                    <span className="ms-1">{levelName}</span>
                                                    <Badge bg="light" text="dark" className="ms-2">{levelCount}</Badge>
                                                </div>

                                                {!levelCollapsed && (
                                                    <div className="ms-4">
                                                        {Array.from(levelGroup.keys()).map(roomKey => {
                                                            const components = levelGroup.get(roomKey)!;
                                                            const roomName = roomKey === '_no_room'
                                                                ? 'Utan rum'
                                                                : components[0]?.roomName || roomKey;
                                                            const roomCollapsed = collapsedRooms.has(`${baseType}_${levelKey}_${roomKey}`);

                                                            // If there's only one component and no room, show it directly
                                                            if (roomKey === '_no_room' && components.length === 1) {
                                                                const comp = components[0];
                                                                return (
                                                                    <Form.Check
                                                                        key={comp.key}
                                                                        type="checkbox"
                                                                        id={`comp-${comp.key}`}
                                                                        label={componentLabel(comp)}
                                                                        checked={selectedComponentKeys.includes(comp.key)}
                                                                        onChange={() => toggleComponent(comp.key)}
                                                                        className="py-1"
                                                                    />
                                                                );
                                                            }

                                                            return (
                                                                <div key={roomKey} className="mb-1">
                                                                    {roomKey !== '_no_room' && (
                                                                        <div
                                                                            className="d-flex align-items-center cursor-pointer py-1"
                                                                            onClick={() => toggleRoomCollapsed(`${baseType}_${levelKey}_${roomKey}`)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            {roomCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                                                            <span className="ms-1 small">{roomName}</span>
                                                                            <Badge bg="light" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                                                {components.length}
                                                                            </Badge>
                                                                        </div>
                                                                    )}

                                                                    {(roomKey === '_no_room' || !roomCollapsed) && (
                                                                        <div className={roomKey !== '_no_room' ? 'ms-4' : ''}>
                                                                            {components.map(comp => (
                                                                                <Form.Check
                                                                                    key={comp.key}
                                                                                    type="checkbox"
                                                                                    id={`comp-${comp.key}`}
                                                                                    label={componentLabel(comp)}
                                                                                    checked={selectedComponentKeys.includes(comp.key)}
                                                                                                onChange={() => toggleComponent(comp.key)}
                                                                                    className="py-1"
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {sortedBaseTypes.length === 0 && (
                    <p className="text-muted mb-0">
                        {typeFilterActive
                            ? `Alla ${getTranslation(suggestedType).toLowerCase()} i byggnaden har redan en kontroll av mallen.`
                            : 'Inga fler komponenter att välja i denna byggnad.'}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <Container fluid className="mt-4 building-check-form-page">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </Container>
        );
    }

    return (
        <>
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={[
                        { text: currentBuilding?.summary?.name || 'Byggnad', link: `/building/${buildingId}` },
                        { text: 'Aktiviteter', link: `/building/${buildingId}/checks` },
                        { text: 'Lägg till' }
                    ]}
                />
            </div>

            <Container fluid className="mt-4 building-check-form-page">
                <h1 className="page-title mb-3">Lägg till aktivitet</h1>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            <Card className="building-check-form">
                <Card.Body className="p-4">
                    {/* Template Selection */}
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">Välj mall *</Form.Label>
                        {availableTemplates.length === 0 ? (
                            <div className="text-center py-4 border rounded">
                                <p className="mb-2">Det finns inga mallar ännu.</p>
                                <div className="d-flex justify-content-center gap-2 flex-wrap">
                                    {isAdmin && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => setShowNewTemplateModal(true)}
                                        >
                                            <Plus size={16} className="me-1" />
                                            Ny mall
                                        </Button>
                                    )}
                                    <Link to="/settings/building-checks" className="btn btn-outline-primary btn-sm">
                                        <Settings size={16} className="me-1" />
                                        Hantera mallar
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex gap-2 flex-wrap">
                                <Form.Select
                                    className="flex-grow-1 w-auto"
                                    value={selectedTemplateId}
                                    onChange={(e) => selectTemplate(e.target.value)}
                                >
                                    <option value="">Välj mall...</option>
                                    {availableTemplates.map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name} ({translateInspectionCategory(template.category)})
                                        </option>
                                    ))}
                                </Form.Select>
                                {isAdmin && (
                                    <Button
                                        variant="outline-primary"
                                        className="text-nowrap"
                                        onClick={() => setShowNewTemplateModal(true)}
                                    >
                                        <Plus size={16} className="me-1" />
                                        Ny mall
                                    </Button>
                                )}
                            </div>
                        )}
                    </Form.Group>

                    {/* Template Info Display */}
                    {selectedTemplate && (
                        <Card className="mb-4 bg-light">
                            <Card.Body className="p-3">
                                <h6 className="mb-3">Mallinformation</h6>
                                <p className="mb-2"><strong>Beskrivning:</strong> {selectedTemplate.description}</p>
                                <div className="row">
                                    <div className="col-md-6">
                                        <p className="mb-1 small">
                                            <strong>Intervall:</strong> {formatInterval(selectedTemplate.interval)}
                                        </p>
                                    </div>
                                    {selectedTemplate.estimatedCost > 0 && (
                                        <div className="col-md-6">
                                            <p className="mb-1 small">
                                                <strong>Kostnad:</strong> {selectedTemplate.estimatedCost} kr
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {selectedTemplate.instructionalAttachments &&
                                    Object.keys(selectedTemplate.instructionalAttachments).length > 0 && (
                                        <p className="mb-1 small">
                                            <strong>Bilagor:</strong>{' '}
                                            {Object.values(selectedTemplate.instructionalAttachments)
                                                .map(a => a.key)
                                                .join(', ')}
                                        </p>
                                    )}
                                <Badge bg="secondary">
                                    {translateInspectionCategory(selectedTemplate.category)}
                                </Badge>
                                {selectedTemplate.regulatoryType && (
                                    <Badge bg="warning" text="dark" className="ms-2">
                                        {translateRegulatoryType(selectedTemplate.regulatoryType)}
                                    </Badge>
                                )}
                                {selectedTemplate.componentSpecificType && (
                                    <Badge bg="info" className="ms-2">
                                        Föreslagen komponenttyp: {getTranslation(selectedTemplate.componentSpecificType)}
                                    </Badge>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Start Date */}
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">Startdatum (första förfallodatum) *</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Form.Group>

                    <hr className="my-4" />

                    <h5 className="mb-3">Anpassningar (valfritt)</h5>

                    {/* Label: tells one check apart; several new checks are named after their components */}
                    {!(linkingMode === 'components' && selectedComponentKeys.length > 1) && (
                    <Form.Group className="mb-4">
                        <Form.Label>Benämning</Form.Label>
                        <Form.Text className="text-muted d-block mb-2">
                            Skiljer kontroller av samma mall åt, t.ex. "Elmätare hus A". Utan benämning visas komponentens namn.
                        </Form.Text>
                        <Form.Control
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            maxLength={100}
                        />
                    </Form.Group>
                    )}

                    {/* Building Notes */}
                    <Form.Group className="mb-4">
                        <Form.Label>Byggnadspecifika anteckningar</Form.Label>
                        <Form.Text className="text-muted d-block mb-2">
                            Läggs till efter mallens beskrivning
                        </Form.Text>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={buildingNotes}
                            onChange={(e) => setBuildingNotes(e.target.value)}
                            placeholder="T.ex. specifika instruktioner för denna byggnad..."
                        />
                    </Form.Group>

                    {/* Custom Interval */}
                    <Form.Group className="mb-4">
                        <Form.Check
                            type="checkbox"
                            id="custom-interval"
                            label="Anpassa intervall"
                            checked={useCustomInterval}
                            onChange={(e) => setUseCustomInterval(e.target.checked)}
                        />
                        {useCustomInterval && (
                            <div className="d-flex gap-2 mt-2">
                                <NumberInput
                                    className="form-control"
                                    min="1"
                                    integer
                                    emptyValue={1}
                                    value={customIntervalValue}
                                    onChange={(value) => setCustomIntervalValue(Math.max(1, value))}
                                    style={{ width: '100px' }}
                                />
                                <Form.Select
                                    value={customIntervalUnit}
                                    onChange={(e) => setCustomIntervalUnit(e.target.value as IntervalUnit)}
                                    style={{ width: '150px' }}
                                >
                                    {INTERVAL_UNITS.map(unit => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        )}
                    </Form.Group>

                    {/* Custom Cost */}
                    <Form.Group className="mb-4">
                        <Form.Label>Anpassa kostnad</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Control
                                type="number"
                                min="0"
                                value={customCost}
                                onChange={(e) => setCustomCost(e.target.value)}
                                placeholder={selectedTemplate?.estimatedCost?.toString() || ''}
                                style={{ width: '150px' }}
                            />
                            <span>kr</span>
                        </div>
                    </Form.Group>

                    <hr className="my-4" />

                    <h5 className="mb-3">Komponent</h5>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="radio"
                            id="link-components"
                            name="linking-mode"
                            label="En kontroll per vald komponent"
                            checked={linkingMode === 'components'}
                            onChange={() => setLinkingMode('components')}
                        />
                        <Form.Check
                            type="radio"
                            id="link-none"
                            name="linking-mode"
                            label="Hela byggnaden (ingen komponent)"
                            checked={linkingMode === 'none'}
                            onChange={() => {
                                setLinkingMode('none');
                                setSelectedComponentKeys([]);
                            }}
                        />
                        <Form.Check
                            type="radio"
                            id="link-location"
                            name="linking-mode"
                            label="Hela byggnaden, med plats på ritning"
                            checked={linkingMode === 'location'}
                            onChange={() => {
                                setLinkingMode('location');
                                setSelectedComponentKeys([]);
                            }}
                        />
                        {unitOptions.length > 0 && (
                            <Form.Check
                                type="radio"
                                id="link-unit"
                                name="linking-mode"
                                label="Länka till lägenhet/lokal"
                                checked={linkingMode === 'unit'}
                                onChange={() => {
                                    setLinkingMode('unit');
                                    setSelectedComponentKeys([]);
                                }}
                            />
                        )}
                    </Form.Group>

                    {linkingMode === 'unit' && (
                        <div className="mb-4">
                            <Form.Label>Välj lägenhet eller lokal</Form.Label>
                            <Form.Select value={selectedUnitKey} onChange={e => setSelectedUnitKey(e.target.value)}>
                                <option value="">Välj…</option>
                                {unitOptions.map(unit => (
                                    <option key={unit.key} value={unit.key}>{unitDisplayName(unit)}</option>
                                ))}
                            </Form.Select>
                            <Form.Text muted>Aktiviteten gäller den valda enheten.</Form.Text>
                        </div>
                    )}

                    {/* Component picker: one check per selected component */}
                    {linkingMode === 'components' && (
                        <div className="mb-4">
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <Form.Label className="mb-0">Välj komponenter</Form.Label>
                                {selectedComponentKeys.length > 0 && (
                                    <Badge bg="primary">{selectedComponentKeys.length} valda</Badge>
                                )}
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="ms-auto"
                                    onClick={toggleAllOffered}
                                    disabled={offeredKeys.length === 0}
                                >
                                    {allOfferedSelected ? 'Avmarkera alla' : 'Välj alla'}
                                </Button>
                            </div>
                            {suggestedType && (
                                <Form.Check
                                    type="switch"
                                    id="filter-by-type"
                                    className="mb-2"
                                    label={`Visa bara ${getTranslation(suggestedType).toLowerCase()} (mallens komponenttyp)`}
                                    checked={filterByType}
                                    onChange={e => changeTypeFilter(e.target.checked)}
                                />
                            )}
                            {renderComponentTree()}
                            <Form.Text muted>
                                Varje vald komponent får en egen kontroll med schemat ovan. Komponenter som redan har en kontroll av mallen visas inte.
                            </Form.Text>
                        </div>
                    )}

                    {/* Location Marker Picker */}
                    {linkingMode === 'location' && currentBuilding && (
                        <div className="mb-4">
                            <LocationMarkerPicker
                                building={currentBuilding}
                                value={selectedLocation}
                                onChange={setSelectedLocation}
                            />
                        </div>
                    )}
                </Card.Body>

                <Card.Footer className="d-flex justify-content-end gap-2">
                    <Link to={`/building/${buildingId}/checks`} className="btn btn-secondary">
                        Avbryt
                    </Link>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!selectedTemplate || !startDate || saving || (linkingMode === 'components' && selectedComponentKeys.length === 0)}
                    >
                        {saving ? 'Sparar...' : (linkingMode === 'components' && selectedComponentKeys.length > 1 ? `Lägg till ${selectedComponentKeys.length} kontroller` : 'Lägg till aktivitet')}
                    </Button>
                </Card.Footer>
            </Card>
            </Container>

            {/* New template (admins): saved and applied to this check */}
            {showNewTemplateModal && (
                <BuildingCheckTemplateModal
                    template={null}
                    excludeAssessment
                    infoText="När mallen är sparad väljs den för aktiviteten du lägger till."
                    onSaved={handleNewTemplateSaved}
                    onCancel={() => setShowNewTemplateModal(false)}
                />
            )}
        </>
    );
};

export default AddBuildingCheckPage;
